import { useState, useEffect } from 'react';
import { formatINR, formatDate } from '../../utils/formatters';
import axios from 'axios';

const portalApi = axios.create({ baseURL: '/api/v1/portal' });

export function ClientPortalPage() {
  const [token, setToken] = useState(localStorage.getItem('portal_token') || '');
  const [loggedIn, setLoggedIn] = useState(!!token);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'invoices' | 'projects' | 'tickets'>('invoices');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await portalApi.post('/auth/login', { email, code });
      const t = res.data.data.token;
      localStorage.setItem('portal_token', t);
      portalApi.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      setToken(t);
      setLoggedIn(true);
    } catch { setError('Invalid email or access code'); }
  };

  useEffect(() => {
    if (token) portalApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, [token]);

  useEffect(() => {
    if (!loggedIn) return;
    setLoading(true);
    portalApi.get(`/client/${tab}`).then(r => setData(r.data.data || [])).catch(() => setData([])).finally(() => setLoading(false));
  }, [loggedIn, tab]);

  const logout = () => { localStorage.removeItem('portal_token'); setLoggedIn(false); setToken(''); };

  if (!loggedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--ink-0)] text-xl font-bold text-white">A</div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Client Portal</h1>
            <p className="text-sm text-gray-500">Sign in with your email and access code</p>
          </div>
          <form onSubmit={login} className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="mb-4"><label className="mb-1 block text-sm font-medium text-gray-700">Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" /></div>
            <div className="mb-6"><label className="mb-1 block text-sm font-medium text-gray-700">Access Code</label><input type="text" value={code} onChange={e => setCode(e.target.value)} required placeholder="6-digit code" className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:border-[var(--ink-0)] focus:outline-none tracking-widest text-center text-lg" maxLength={6} /></div>
            <button type="submit" className="w-full rounded-lg bg-[var(--ink-0)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)]">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--ink-0)] text-sm font-bold text-white">A</div>
            <span className="text-lg font-bold text-gray-900">Client Portal</span>
          </div>
          <button onClick={logout} className="text-sm text-gray-500 hover:text-red-600">Logout</button>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-6 flex gap-1 rounded-lg bg-gray-100 p-1">
          {(['invoices', 'projects', 'tickets'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`flex-1 rounded-md px-4 py-2 text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white text-[var(--ink-0)] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>My {t}</button>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--ink-0)] border-t-transparent" /></div> : data.length === 0 ? <p className="py-16 text-center text-sm text-gray-400">No {tab} found</p> : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50"><tr>
                {tab === 'invoices' && <><th className="px-4 py-3 text-left font-medium text-gray-500">Number</th><th className="px-4 py-3 text-left font-medium text-gray-500">Status</th><th className="px-4 py-3 text-left font-medium text-gray-500">Date</th><th className="px-4 py-3 text-right font-medium text-gray-500">Total</th><th className="px-4 py-3 text-right font-medium text-gray-500">Due</th></>}
                {tab === 'projects' && <><th className="px-4 py-3 text-left font-medium text-gray-500">Name</th><th className="px-4 py-3 text-left font-medium text-gray-500">Status</th><th className="px-4 py-3 text-left font-medium text-gray-500">Progress</th><th className="px-4 py-3 text-left font-medium text-gray-500">Deadline</th></>}
                {tab === 'tickets' && <><th className="px-4 py-3 text-left font-medium text-gray-500">Number</th><th className="px-4 py-3 text-left font-medium text-gray-500">Title</th><th className="px-4 py-3 text-left font-medium text-gray-500">Status</th><th className="px-4 py-3 text-left font-medium text-gray-500">Priority</th></>}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((d: any) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    {tab === 'invoices' && <><td className="px-4 py-3 font-medium text-[var(--ink-0)]">{d.number}</td><td className="px-4 py-3"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{d.status}</span></td><td className="px-4 py-3 text-gray-600">{formatDate(d.issueDate)}</td><td className="px-4 py-3 text-right font-medium">{formatINR(d.total)}</td><td className="px-4 py-3 text-right text-red-600">{formatINR(d.amountDue)}</td></>}
                    {tab === 'projects' && <><td className="px-4 py-3 font-medium">{d.name}</td><td className="px-4 py-3"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{d.status}</span></td><td className="px-4 py-3"><div className="h-2 w-24 rounded-full bg-gray-200"><div className="h-2 rounded-full bg-[var(--ink-0)]" style={{ width: `${d.progress || 0}%` }} /></div></td><td className="px-4 py-3 text-gray-600">{d.deadline ? formatDate(d.deadline) : '—'}</td></>}
                    {tab === 'tickets' && <><td className="px-4 py-3 font-mono text-xs">{d.number}</td><td className="px-4 py-3 font-medium">{d.title}</td><td className="px-4 py-3"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{d.status}</span></td><td className="px-4 py-3"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{d.priority}</span></td></>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
